/**
 * Hub `/admin` — visão geral (T6, variante 1a: cards por domínio + busca +
 * coluna de contexto). Prancha: `design-references/admin-hub/round-1/
 * "01 Hub Administracao (visao geral).dc.html"`, opção 1a.
 *
 * REGRA INVIOLÁVEL (Global Constraint do plano — "1 caminho de escrita por
 * dado"): o hub NUNCA edita, só resume e aponta. Não há nenhum controle de
 * escrita nesta árvore — nem toggle, nem input de valor. O único campo é a
 * busca (leitura sobre `searchCatalog`), e o teste de ausência garante isso
 * (`AdminHubPage.spec.tsx`).
 *
 * `omitWhenBarred` (P19 — nav-model.ts:9-17): cap ausente ⇒ item AUSENTE, não
 * locked. `resolveEstado` não consome essa flag (devolve `'locked'`); quem
 * omite é o consumidor. Este arquivo é o 3º consumidor do catálogo (depois
 * do rail de `AdminShell` e do rail de âncoras de `DomainPage`) e tem DUAS
 * superfícies que precisam filtrar por conta própria: os cards (corpo) e os
 * resultados da busca — `isBarred()` é a fonte única das duas, lendo
 * `capabilities` (mesmo array de `useCapabilities()`/`useCapability()`) para
 * nunca poder discordar do rail/seção sobre o mesmo item.
 *
 * Coluna de contexto — fidelidade (divergência DECLARADA): o brief de T6
 * enumera só (a) "Pede sua atenção" e (b) "Mudanças recentes". A prancha 01,
 * variante 1a, mostra AINDA um 3º bloco "Gestão" (Usuários / Explorador de
 * auditoria) abaixo dos outros dois, na ordem Mudanças → Atenção → Gestão.
 * Seguindo a regra de precedência da fidelidade (prancha vence quando diverge
 * do texto do brief), este arquivo replica os 3 blocos nessa ordem. O bloco
 * Gestão é só navegação (mesmas capabilities do rail de `AdminShell.tsx`:
 * `users-read` / `audit-events-read`) — não introduz nenhuma escrita nova.
 *
 * Cabeçalho — divergência DECLARADA: a prancha mostra uma sublinha com dados
 * do tenant (nome, plano, canais, operadores) + um badge "Operação saudável".
 * Esta task não tem fonte para esses números (nenhum hook do catálogo de
 * fontes citado no brief os produz) — inventá-los violaria a regra "o hub
 * não mente" (estado-resolver.ts). Cabeçalho aqui fica só eyebrow + h1, e o
 * dado real fica para uma task futura que traga a fonte.
 *
 * Auditoria: `AuditListFilters.entityType` é uma string, não array — o
 * Engine emite `'tenant'` (config + perfil de operação) e `'tenant_branding'`
 * (identidade visual) para o que este hub cobre (context.md, medido
 * 22/Ago). Duas chamadas a `useAuditEvents`, mescladas e ordenadas por
 * `occurredAt` (nunca `updatedAt` — relógio de mutação, não posição no
 * tempo), cortadas em 8. Gate no FETCH (`enabled`), não só no render — sem
 * `audit-events-read` nenhuma requisição sai.
 */
import { ChevronRight, ScrollText, Search, UserCog } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCapabilities, useCapability } from '../../../hooks/use-capability';
import { relativeTime } from '../../conversations/format';
import { useAuditEvents } from '../audit/hooks/use-audit-events';

import { CONFIG_CATALOG, catalogTarget, DOMINIOS, searchCatalog, secoesByDominio } from './catalog';
import { EstadoBadge } from './EstadoBadge';
import { useConfigEstados } from './use-config-estados';

import type { ConfigCatalogItem, DominioDef } from './catalog';
import type { EstadoConfig } from './estado-resolver';
import type { AuditEventWire } from '../audit/types';

/** Fonte única de "barrado" — cards e busca leem exatamente a mesma regra. */
function isBarred(item: ConfigCatalogItem, capabilities: readonly string[]): boolean {
  return item.omitWhenBarred === true && !capabilities.includes(item.capability);
}

/**
 * Domínio de um item — extraído porque `DOMINIOS.find((d) => d.id === item.dominio)!`
 * apareceu em 3 call sites (fix round 1, Finding 4 do coordenador): a linha de
 * resultado da busca, a lista de "Pede sua atenção" e (indiretamente) `catalogTarget`.
 */
function domFor(item: Pick<ConfigCatalogItem, 'dominio'>): DominioDef {
  return DOMINIOS.find((d) => d.id === item.dominio)!;
}

function HubSearch({
  estados,
  capabilities,
}: {
  readonly estados: Map<string, EstadoConfig | null>;
  readonly capabilities: readonly string[];
}): JSX.Element {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');

  const results = useMemo(() => {
    if (term.trim().length === 0) return [];
    return searchCatalog(term).filter((i) => !isBarred(i, capabilities));
  }, [term, capabilities]);

  function go(item: ConfigCatalogItem): void {
    navigate(catalogTarget(item));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' && results.length > 0) {
      go(results[0]!);
    }
  }

  return (
    <div className="relative max-w-2xl">
      <div className="flex items-center gap-3 rounded-lg border border-border-subtle bg-surface px-4 py-2.5 shadow-sm">
        <Search aria-hidden className="h-4 w-4 flex-shrink-0 text-text-secondary" />
        <input
          type="text"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar configuração… “lembrete”, “conduta”, “failover”"
          aria-label="Buscar configuração"
          className="w-full border-0 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-secondary"
        />
      </div>
      {results.length > 0 && (
        <ul
          role="listbox"
          aria-label="Resultados da busca"
          className="absolute z-10 mt-1 w-full rounded-lg border border-border-subtle bg-surface shadow-md"
        >
          {results.map((item) => {
            const dominio = domFor(item);
            return (
              <li key={item.id} role="option" aria-selected={false}>
                <a
                  href={catalogTarget(item)}
                  onClick={(e) => {
                    e.preventDefault();
                    go(item);
                  }}
                  className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-surface-raised"
                >
                  <span className="min-w-0 flex-1 truncate text-text-primary">
                    {/* Natureza no rótulo (prancha 03/3a): Seção aterrissa na âncora e salva
                        na barra; Ferramenta abre página própria — a surpresa some antes do clique. */}
                    {`${item.natureza === 'ferramenta' ? 'Ferramenta' : 'Seção'} · ${item.label} · ${dominio.label}`}
                  </span>
                  <EstadoBadge estado={estados.get(item.id)} item={item} />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function DomainCard({
  dominio,
  estados,
  capabilities,
}: {
  readonly dominio: DominioDef;
  readonly estados: Map<string, EstadoConfig | null>;
  readonly capabilities: readonly string[];
}): JSX.Element {
  const navigate = useNavigate();
  // Cards resumem as SEÇÕES (estado por seção); ferramentas chegam pela busca e pela
  // página do domínio (D5) — o card não tem fonte de estado para elas (o hub não chuta).
  const itens = secoesByDominio(dominio.id).filter((i) => !isBarred(i, capabilities));
  const MAX_SHOWN = 3;
  const shown = itens.slice(0, MAX_SHOWN);
  const restCount = itens.length - shown.length;

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`Abrir domínio ${dominio.label}`}
      onClick={() => navigate(dominio.rota)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') navigate(dominio.rota);
      }}
      className="cursor-pointer rounded-lg border border-border-subtle bg-surface shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex items-center gap-2 px-4 pb-2 pt-4">
        <span className="text-sm font-semibold text-text-primary">{dominio.label}</span>
        <span className="ml-auto text-xs text-text-secondary">
          {itens.length} {itens.length === 1 ? 'item' : 'itens'}
        </span>
        <ChevronRight aria-hidden className="h-4 w-4 text-text-secondary" />
      </div>
      <ul className="flex flex-col px-4 pb-3">
        {shown.map((item) => (
          <li key={item.id} className="border-t border-border-subtle first:border-t-0">
            <a
              href={catalogTarget(item)}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(catalogTarget(item));
              }}
              className="flex items-center gap-2 py-1.5 text-sm text-text-secondary hover:text-text-primary"
            >
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              <EstadoBadge estado={estados.get(item.id)} item={item} />
            </a>
          </li>
        ))}
        {restCount > 0 && (
          <li className="border-t border-border-subtle pt-1.5 text-xs text-text-secondary">
            +{restCount} mais
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * Mescla duas listas de `useAuditEvents` (um `entityType` cada) por
 * `occurredAt` desc, cortadas em 8.
 *
 * Fix round 1 (Finding 1 do coordenador — Important): a versão anterior só
 * devolvia `{items, isLoading}` — se UMA das duas queries falhasse (rede,
 * 5xx), essa fonte contribuía zero itens para a mescla e o render caía no
 * mesmo texto de "sem mudanças nenhuma", uma meia-verdade indistinguível de
 * "carreguei tudo e realmente não há nada". `isError` agora é rastreado e
 * distinguido no render — a mesma honestidade que `EstadoBadge` já aplica a
 * `indisponivel` (nunca chutar um estado que a fonte não confirmou).
 *
 * Fix round 1 (Finding 3 — Minor, mesma vizinhança): quando uma query já
 * resolveu com itens e a outra ainda está carregando, `items.length > 0`
 * pulava o check de `isLoading` no render antigo e mostrava uma lista
 * possivelmente incompleta sem nenhuma pista. `isLoading` continua exposto
 * para o consumidor decidir mostrar essa pista (ver render abaixo).
 */
function useMudancasRecentes(enabled: boolean): {
  items: AuditEventWire[];
  isLoading: boolean;
  isError: boolean;
} {
  const tenant = useAuditEvents({ entityType: 'tenant', limit: 8 }, enabled);
  const branding = useAuditEvents({ entityType: 'tenant_branding', limit: 8 }, enabled);

  const items = useMemo(() => {
    const a = tenant.data?.pages.flatMap((p) => p.items) ?? [];
    const b = branding.data?.pages.flatMap((p) => p.items) ?? [];
    return [...a, ...b]
      .sort((x, y) => new Date(y.occurredAt).getTime() - new Date(x.occurredAt).getTime())
      .slice(0, 8);
  }, [tenant.data, branding.data]);

  return {
    items,
    isLoading: enabled && (tenant.isLoading || branding.isLoading),
    isError: enabled && (tenant.isError || branding.isError),
  };
}

export function AdminHubPage(): JSX.Element {
  const navigate = useNavigate();
  const capabilities = useCapabilities();
  const canAudit = useCapability('audit-events-read');
  const canUsers = useCapability('users-read');
  const estados = useConfigEstados();
  const mudancas = useMudancasRecentes(canAudit);

  // "Pede sua atenção": nao-configurado só ocorre com a capability já ativa
  // (resolveEstado devolve 'locked' antes de chegar ao resolver quando a cap
  // falta) — o check explícito abaixo é defensivo, não decorativo: garante
  // que este bloco nunca aponte para algo que o papel não pode configurar,
  // mesmo se um resolver futuro quebrar essa premissa sem avisar aqui.
  const atencao = useMemo(
    () =>
      CONFIG_CATALOG.filter(
        (i) => estados.get(i.id) === 'nao-configurado' && capabilities.includes(i.capability),
      ),
    [estados, capabilities],
  );

  return (
    <div className="p-6 lg:p-8">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
        Administração
      </p>
      <h1 className="mt-1 text-2xl font-semibold text-text-primary">Visão geral do tenant</h1>

      <div className="mt-5">
        <HubSearch estados={estados} capabilities={capabilities} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[1fr_300px]">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {DOMINIOS.map((d) => (
            <DomainCard key={d.id} dominio={d} estados={estados} capabilities={capabilities} />
          ))}
        </div>

        <aside className="flex flex-col gap-4">
          {canAudit && (
            <div className="rounded-lg border border-border-subtle bg-surface p-4 shadow-sm">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Mudanças recentes
              </p>
              {mudancas.isError && (
                <p className="mb-2 text-xs text-amber-700">
                  Não foi possível carregar as mudanças recentes.
                </p>
              )}
              {mudancas.items.length > 0 ? (
                <>
                  <ul className="flex flex-col gap-2">
                    {mudancas.items.map((event) => (
                      <li
                        key={event.id}
                        className="flex items-baseline gap-2 border-t border-border-subtle pt-2 text-xs first:border-t-0 first:pt-0"
                      >
                        <span className="min-w-0 flex-1 truncate text-text-secondary">
                          {event.actorName ?? 'Sistema'} · {event.eventType}
                        </span>
                        <span className="flex-shrink-0 font-mono text-[10px] text-text-secondary">
                          {relativeTime(event.occurredAt)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {mudancas.isLoading && !mudancas.isError && (
                    <p className="mt-2 text-[10px] text-text-secondary">Carregando mais…</p>
                  )}
                </>
              ) : (
                !mudancas.isError && (
                  <p className="text-xs text-text-secondary">
                    {mudancas.isLoading ? 'Carregando…' : 'Nenhuma mudança recente.'}
                  </p>
                )
              )}
            </div>
          )}

          {atencao.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-800">
                Pede sua atenção
              </p>
              <ul className="flex flex-col gap-2">
                {atencao.map((item) => (
                  <li key={item.id} className="text-sm text-amber-900">
                    <a
                      href={catalogTarget(item)}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(catalogTarget(item));
                      }}
                      className="underline-offset-2 hover:underline"
                    >
                      {item.label} — {item.efeito}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(canUsers || canAudit) && (
            <div className="rounded-lg border border-border-subtle bg-surface p-4 shadow-sm">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Gestão
              </p>
              <ul className="flex flex-col">
                {canUsers && (
                  <li className="border-t border-border-subtle first:border-t-0">
                    <a
                      href="/admin/users"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/admin/users');
                      }}
                      className="flex items-center gap-2 py-1.5 text-sm text-text-secondary hover:text-text-primary"
                    >
                      <UserCog aria-hidden className="h-4 w-4" />
                      Usuários e convites
                    </a>
                  </li>
                )}
                {canAudit && (
                  <li className="border-t border-border-subtle first:border-t-0">
                    <a
                      href="/admin/audit"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/admin/audit');
                      }}
                      className="flex items-center gap-2 py-1.5 text-sm text-text-secondary hover:text-text-primary"
                    >
                      <ScrollText aria-hidden className="h-4 w-4" />
                      Explorador de auditoria
                    </a>
                  </li>
                )}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
